class SimpleRateLimiter {
    constructor(limit = 2000, windowMs = 300000) {
        this.limit = limit;
        this.windowMs = windowMs;
        this.requestCounts = new Map();
        this.loadFromStorage();
        // Add periodic cleanup
        this.cleanupInterval = setInterval(() => this.cleanup(), this.windowMs);
    }

    loadFromStorage() {
        try {
            const storedData = localStorage.getItem('rateLimiterData');
            if (storedData) {
                const { counts, timestamps } = JSON.parse(storedData);
                // Only keep data that's within the time window
                const now = Date.now();
                Object.entries(counts).forEach(([userId, count]) => {
                    const timestamp = timestamps[userId];
                    if (now - timestamp < this.windowMs) {
                        this.requestCounts.set(userId, { count, timestamp });
                    }
                });
            }
        } catch (error) {
            // Silently handle the error
        }
    }

    saveToStorage() {
        try {
            const counts = {};
            const timestamps = {};
            this.requestCounts.forEach((value, key) => {
                counts[key] = value.count;
                timestamps[key] = value.timestamp;
            });
            localStorage.setItem('rateLimiterData', JSON.stringify({ counts, timestamps }));
        } catch (error) {
            // Silently handle the error
        }
    }

    cleanup() {
        const now = Date.now();
        for (const [userId, data] of this.requestCounts.entries()) {
            if (now - data.timestamp >= this.windowMs) {
                this.requestCounts.delete(userId);
            }
        }
        this.saveToStorage();
    }

    canMakeRequest(userId) {
        const now = Date.now();
        const userData = this.requestCounts.get(userId);

        if (!userData || now - userData.timestamp >= this.windowMs) {
            // Reset if window has passed
            this.requestCounts.set(userId, { count: 1, timestamp: now });
            this.saveToStorage();
            return true;
        }

        if (userData.count >= this.limit) {
            return false;
        }

        // Increment count
        userData.count++;
        this.requestCounts.set(userId, userData);
        this.saveToStorage();
        return true;
    }

    getRemainingRequests(userId) {
        const userData = this.requestCounts.get(userId);
        if (!userData || Date.now() - userData.timestamp >= this.windowMs) {
            return this.limit;
        }
        return Math.max(0, this.limit - userData.count);
    }

    getResetTime(userId) {
        const userData = this.requestCounts.get(userId);
        if (!userData) return 0;
        return userData.timestamp + this.windowMs;
    }

    // Add cleanup in destructor
    destroy() {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
        this.cleanup();
        this.requestCounts.clear();
    }
}

// Create a single instance
const rateLimiter = new SimpleRateLimiter();

// Add cleanup on page unload
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        rateLimiter.destroy();
    });
}

export { rateLimiter };